import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useHistory, useLocation } from 'react-router-dom';
import SubscriptionManageModal from 'components/shared/SubscriptionManageModal';
import SubscriptionCancellationModal from 'components/shared/SubscriptionCancellationModal';
import BuyCreditsModal from 'components/shared/BuyCreditsModal';
import PaymentStatusModal from 'components/shared/PaymentStatusModal';
import axiosPrivate from 'config/axiosPrivate';
import './Subscription.css';
import usePageTracking from 'utils/hooks/usePageTracking';

const ADMIN_CLIENT_SUBSCRIPTION_PAGE = '/AdminClient/Abonnement';

const Subscription = () => {
  usePageTracking(ADMIN_CLIENT_SUBSCRIPTION_PAGE);

  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const [isManageModalOpen, setIsManageModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isBuyCreditsModalOpen, setIsBuyCreditsModalOpen] = useState(false);
  const [paymentStatusModal, setPaymentStatusModal] = useState(null);
  const [siteCount, setSiteCount] = useState(3);
  const [adminCount, setAdminCount] = useState(0);
  const [editorCount, setEditorCount] = useState(0);
  const [projectManagerCount, setProjectManagerCount] = useState(0);
  const [isUpdatingSubscription, setIsUpdatingSubscription] = useState(false);
  const [updateSubscriptionError, setUpdateSubscriptionError] = useState('');
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [companyQuotas, setCompanyQuotas] = useState(null);
  const [websiteQuotas, setWebsiteQuotas] = useState(null);
  const [websiteSummaries, setWebsiteSummaries] = useState([]);
  const [sessionUserEmail, setSessionUserEmail] = useState('');
  
  const planUnitPrices = {
    free: 0,
    light: 9.9,
    premium: 49.9,
    pro: 99.9,
    custom: 0,
  };
  const hasPlanKey = Boolean(companyQuotas?.planKey);
  const normalizedPlanKey = String(companyQuotas?.planKey || 'light').toLowerCase();
  const normalizedSubscriptionStatus = String(companyQuotas?.subscriptionStatus || '').trim().toUpperCase();
  const formattedPlanKey = companyQuotas?.planKey
    ? String(companyQuotas.planKey).charAt(0).toUpperCase() +
      String(companyQuotas.planKey).slice(1).toLowerCase()
    : 'Light';
  const unitPrice = Number(planUnitPrices[normalizedPlanKey] ?? companyQuotas?.websitePricePerSite ?? 9.9);
  const monthlyTotal = (siteCount * unitPrice).toFixed(2).replace('.', ',');
  const formattedUnitPrice = unitPrice.toFixed(2).replace('.', ',');
  const subscribedMaxWebsites = Math.max(1, Number(companyQuotas?.maxWebsites ?? 1));
  const activeSiteCount = Math.max(1, Number(companyQuotas?.currentWebsites ?? 1));
  const upgradeByPlan = {
    free: {
      text: t('Passez au forfait light pour 9,90€'),
      buttonLabel: t('Améliorer'),
      benefits: [
        t('1 website'),
        t('1 admin'),
        t('2 Agents IA'),
        t('200 crédit IA'),
        t('20 textes'),
        t('GPT ou IA utilisateur'),
      ],
    },
    light: {
      text: t('Passez au forfait premium pour 49,90€'),
      buttonLabel: t('Améliorer'),
      benefits: [
        t('1 website'),
        t('1 admin + 1 redacteur'),
        t('3 Agents IA'),
        t('1000 crédit IA'),
        t('100 textes'),
        t('GPT +Claude'),
      ],
    },
    premium: {
      text: t('Passez au forfait pro pour 99,90€'),
      buttonLabel: t('Améliorer'),
      benefits: [
        t('1 website'),
        t('1 admin + 1 redacteur + 1 chef de projet'),
        t('5 Agents IA'),
        t('2000 crédit IA'),
        t('200 textes'),
        t('GPT +Claude + perplexity+ hybrid'),
      ],
    },
  };
  const currentUpgradeConfig = upgradeByPlan[normalizedPlanKey] || null;
  const totalCredits = Number(companyQuotas?.totalCredits ?? 0);
  const usedCreditsRaw = companyQuotas?.usedCredits ?? companyQuotas?.consumedCredits;
  const remainingCreditsRaw = companyQuotas?.remainingCredits ?? companyQuotas?.remainingCredit;
  const usedCredits =
    Number.isFinite(Number(usedCreditsRaw))
      ? Number(usedCreditsRaw)
      : Number.isFinite(Number(remainingCreditsRaw)) && totalCredits > 0
        ? Math.max(0, totalCredits - Number(remainingCreditsRaw))
        : 0;
  const boundedUsedCredits =
    totalCredits > 0 ? Math.min(Math.max(usedCredits, 0), totalCredits) : Math.max(usedCredits, 0);
  const creditProgressPercent =
    totalCredits > 0 ? Math.min(100, Math.max(0, (boundedUsedCredits / totalCredits) * 100)) : 0;
  const isCreditsEmpty = totalCredits > 0 && boundedUsedCredits >= totalCredits;
  const creditStatusText = isCreditsEmpty ? t('Crédits épuisés') : t('Crédits disponibles');
  const creditAmountText = `${boundedUsedCredits}/${totalCredits} ${t('crédits')}`;
  const totalTexts = Math.max(0, Number(websiteQuotas?.maxTexts ?? 0));
  const usedTexts = Math.max(0, Number(websiteQuotas?.usedTexts ?? 0));
  const boundedUsedTexts =
    totalTexts > 0 ? Math.min(usedTexts, totalTexts) : usedTexts;
  const textProgressPercent =
    totalTexts > 0 ? Math.min(100, Math.max(0, (boundedUsedTexts / totalTexts) * 100)) : 0;
  const isTextsEmpty = totalTexts > 0 && boundedUsedTexts >= totalTexts;
  const textStatusText = isTextsEmpty ? t('Contenus épuisés') : t('Mes contenus');
  const textAmountText = `${boundedUsedTexts}/${totalTexts} ${t('textes')}`;
  const emailInitials = (sessionUserEmail || '?')
    .trim()
    .slice(0, 2)
    .toUpperCase();
  const currentWebsiteCount = Math.max(0, Number(companyQuotas?.currentWebsites ?? 0));
  const maxWebsiteCount = Math.max(0, Number(companyQuotas?.maxWebsites ?? 0));
  const currentAdminCount = Math.max(0, Number(companyQuotas?.currentAdmins ?? 0));
  const maxAdminCount = Math.max(0, Number(companyQuotas?.maxAdmins ?? 0));
  const currentEditorCount = Math.max(0, Number(companyQuotas?.currentEditors ?? 0));
  const maxEditorCount = Math.max(0, Number(companyQuotas?.maxEditors ?? 0));
  const currentProjectManagerCount = Math.max(0, Number(companyQuotas?.currentProjectManagers ?? 0));
  const maxProjectManagerCount = Math.max(0, Number(companyQuotas?.maxProjectManagers ?? 0));
  const currentAiAgents = Math.max(0, Number(websiteQuotas?.currentAiAgents ?? 0));
  const maxAiAgents = Math.max(0, Number(websiteQuotas?.maxAiAgents ?? 0));
  const normalizeUrl = (input) =>
    (input || '')
      .toString()
      .trim()
      .toLowerCase()
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .replace(/\/+$/, '');
  const buildWebsiteHref = (input) => {
    const normalized = normalizeUrl(input);
    return normalized ? `https://${normalized}` : '#';
  };
  const formatStatNumber = (value) => Number(value || 0).toLocaleString('fr-FR');
  const formatQuotaValue = (current, max) =>
    `${formatStatNumber(current)} / ${formatStatNumber(max)}`;

  useEffect(() => {
    let isMounted = true;

    const fetchQuotas = async () => {
      try {
        const rawUser = localStorage.getItem('user');
        if (rawUser && isMounted) {
          try {
            const userData = JSON.parse(rawUser);
            const emailFromStorage =
              userData?.user?.mail ||
              userData?.user?.email ||
              userData?.email ||
              '';
            setSessionUserEmail(emailFromStorage);
          } catch (parseError) {
            console.error('Erreur parsing user session:', parseError);
          }
        }

        const [{ data: companyData }, { data: websitesData }] = await Promise.all([
          axiosPrivate.get('/project/company-quotas'),
          axiosPrivate.get('/project/website/all'),
        ]);

        const selectedWebsiteUrl = localStorage.getItem('AC_Dashboard_SelectedWebsite') || '';
        const normalizedSavedUrl = normalizeUrl(selectedWebsiteUrl);
        const websitesList = Array.isArray(websitesData) ? websitesData : [];
        const websiteQuotaResults = await Promise.allSettled(
          websitesList.map(async (website) => {
            if (!website?.id) return null;

            const websiteLabel =
              normalizeUrl(website?.urlSite || website?.url || website?.name) ||
              website?.urlSite ||
              website?.url ||
              website?.name ||
              t('Site web');

            const { data } = await axiosPrivate.get(`/project/website-quotas/${website.id}`);

            return {
              websiteId: website.id,
              websiteLabel,
              quotas: data || null,
            };
          })
        );
        const normalizedWebsiteSummaries = websiteQuotaResults
          .map((result) => (result.status === 'fulfilled' ? result.value : null))
          .filter(Boolean);
        const selectedWebsite = websitesList.find(
          (website) => normalizeUrl(website?.urlSite || website?.url || website?.name) === normalizedSavedUrl
        );
        const websiteId = selectedWebsite?.id;
        const selectedWebsiteSummary =
          normalizedWebsiteSummaries.find((website) => website.websiteId === websiteId) ||
          normalizedWebsiteSummaries[0] ||
          null;
        const websiteData = selectedWebsiteSummary?.quotas || null;

        if (isMounted) {
          setCompanyQuotas(companyData || null);
          setWebsiteQuotas(websiteData);
          setWebsiteSummaries(
            normalizedWebsiteSummaries.map((website) => ({
              websiteId: website.websiteId,
              websiteLabel: website.websiteLabel,
              currentAiAgents: Math.max(0, Number(website.quotas?.currentAiAgents ?? 0)),
              maxAiAgents: Math.max(0, Number(website.quotas?.maxAiAgents ?? 0)),
            }))
          );
          setSiteCount(Math.max(1, Number(companyData?.maxWebsites ?? 1)));
          setAdminCount(Math.max(0, Number(companyData?.maxAdmins ?? 0)));
          setEditorCount(Math.max(0, Number(companyData?.maxEditors ?? 0)));
          setProjectManagerCount(Math.max(0, Number(companyData?.maxProjectManagers ?? 0)));
        }
      } catch (error) {
        console.error('Erreur récupération quotas:', error);
      }
    };

    fetchQuotas();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchInvoices = async () => {
      try {
        const { data } = await axiosPrivate.get('/payment/api/subscriptions/invoices');
        if (!isMounted) return;
        const normalizedInvoices = Array.isArray(data)
          ? data.map((invoice, index) => ({
              id: `${invoice?.date || 'invoice'}-${index}`,
              invoiceId: invoice?.invoiceId || invoice?.id || null,
              month: invoice?.date || '-',
              amount: `${Number(invoice?.totalAmount ?? 0)}€`,
              formula: `Formule ${invoice?.packageName || '-'}`,
              sites: `${Number(invoice?.quantity ?? 0)} site web`,
              selected: index === 0,
            }))
          : [];
        setInvoices(normalizedInvoices);
      } catch (error) {
        if (!isMounted) return;
        setInvoices([]);
      }
    };

    fetchInvoices();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const successParam = searchParams.get('success') ?? searchParams.get('succes');
    if (successParam === 'true') {
      const packTypeFromUrl = (searchParams.get('packType') || '').toUpperCase();
      const creditsByPackType = {
        SMALL: 100,
        MEDIUM: 200,
        LARGE: 500,
        XL: 1000,
      };
      const matchedCredits = creditsByPackType[packTypeFromUrl];
      const date = searchParams.get('date') || new Date().toLocaleDateString('fr-FR');

      if (matchedCredits) {
        setPaymentStatusModal({
          type: 'success',
          flow: 'credits',
          packType: packTypeFromUrl,
          credits: `${matchedCredits} crédits`,
          date,
        });
        return;
      }

      const planFromUrl = searchParams.get('planKey') || searchParams.get('planName') || '—';
      const planName =
        typeof planFromUrl === 'string'
          ? `${planFromUrl.charAt(0).toUpperCase()}${planFromUrl.slice(1).toLowerCase()}`
          : '—';
      const credits = searchParams.get('credits') || '—';
      setPaymentStatusModal({
        type: 'success',
        flow: 'subscription',
        planName,
        credits,
        date,
      });
      return;
    }

    if (successParam === 'false') {
      setPaymentStatusModal({ type: 'cancel' });
      return;
    }

    setPaymentStatusModal(null);
  }, [location.search]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const shouldOpenManageSubscription =
      searchParams.get('openManageSubscription') === '1' ||
      searchParams.get('openManageSubscription') === 'true';
    if (!shouldOpenManageSubscription) return;

    const normalizedCurrentPlan = String(companyQuotas?.planKey || '').trim().toLowerCase();
    if (!normalizedCurrentPlan || normalizedCurrentPlan === 'free' || normalizedCurrentPlan === 'custom') return;

    setIsManageModalOpen(true);
  }, [location.search, companyQuotas]);

  const closePaymentStatusModal = () => {
    const shouldReloadAfterClose =
      paymentStatusModal?.type === 'success' && paymentStatusModal?.flow === 'subscription';
    setPaymentStatusModal(null);
    history.replace('/AdminClient/Abonnement');
    if (shouldReloadAfterClose) {
      window.location.reload();
    }
  };

  const extractCheckoutUrl = (payload) =>
    payload?.checkoutUrl ||
    payload?.url ||
    payload?.data?.checkoutUrl ||
    payload?.data?.url ||
    (typeof payload === 'string' ? payload : null);

  const updateSubscriptionQuantity = async () => {
    const currentMaxWebsites = Math.max(0, Number(companyQuotas?.maxWebsites ?? 0));
    const currentMaxAdmins = Math.max(0, Number(companyQuotas?.maxAdmins ?? 0));
    const currentMaxEditors = Math.max(0, Number(companyQuotas?.maxEditors ?? 0));
    const currentMaxProjectManagers = Math.max(0, Number(companyQuotas?.maxProjectManagers ?? 0));
    const websitesToAdd = Math.max(0, Number(siteCount) - currentMaxWebsites);
    const adminsToAdd = Math.max(0, Number(adminCount) - currentMaxAdmins);
    const writersToAdd = Math.max(0, Number(editorCount) - currentMaxEditors);
    const managersToAdd = Math.max(0, Number(projectManagerCount) - currentMaxProjectManagers);

    if ((websitesToAdd <= 0 && adminsToAdd <= 0 && writersToAdd <= 0 && managersToAdd <= 0) || isUpdatingSubscription) {
      setUpdateSubscriptionError('Veuillez ajouter au moins une ressource ou un utilisateur pour continuer.');
      return;
    }

    setIsUpdatingSubscription(true);
    setUpdateSubscriptionError('');

    try {
      const response = await axiosPrivate.patch('https://typix-dev.azure-api.net/payment/api/subscriptions/update-quantity', null, {
        params: { websitesToAdd, managersToAdd, adminsToAdd, writersToAdd },
      });

      const checkoutUrl = extractCheckoutUrl(response?.data);
      if (checkoutUrl) {
        window.location.assign(checkoutUrl);
        return;
      }

      setIsUpdatingSubscription(false);
      setIsManageModalOpen(false);
      window.location.reload();
    } catch (error) {
      setUpdateSubscriptionError("Impossible de lancer le paiement pour le moment. Merci de réessayer.");
      setIsUpdatingSubscription(false);
    }
  };

  const changePaymentMethod = async () => {
    const searchParams = new URLSearchParams(location.search);
    const packTypeFromUrl = (searchParams.get('packType') || '').toUpperCase();
    const planFromUrl = (searchParams.get('planKey') || searchParams.get('planName') || '').toUpperCase();

    try {
      if (packTypeFromUrl) {
        const response = await axiosPrivate.get('/payment/api/subscriptions/generate-credits-checkout-url', {
          params: { creditPack: packTypeFromUrl },
        });
        const checkoutUrl = extractCheckoutUrl(response?.data);
        if (checkoutUrl) {
          window.location.assign(checkoutUrl);
          return;
        }
      } else if (planFromUrl) {
        const response = await axiosPrivate.get('/payment/api/subscriptions/generate-checkout-url', {
          params: {
            planKey: planFromUrl,
            quantity: Math.max(1, Number(siteCount || companyQuotas?.currentWebsites || 1)),
          },
        });
        const checkoutUrl = extractCheckoutUrl(response?.data);
        if (checkoutUrl) {
          window.location.assign(checkoutUrl);
          return;
        }
      }
    } catch (error) {
      console.error('Erreur relance paiement:', error);
    }

    setPaymentStatusModal(null);
    history.replace('/AdminClient/Abonnement');
  };

  const retryPayment = () => {
    const searchParams = new URLSearchParams(location.search);
    const packTypeFromUrl = searchParams.get('packType');
    if (packTypeFromUrl) {
      setPaymentStatusModal(null);
      setIsBuyCreditsModalOpen(true);
      history.replace('/AdminClient/Abonnement');
      return;
    }

    const planFromUrl = (searchParams.get('planKey') || 'PREMIUM').toLowerCase();
    setPaymentStatusModal(null);
    history.replace(`/AdminClient/Forfait?plan=${planFromUrl}`);
  };

  const downloadInvoicePdf = async (invoice) => {
    if (!invoice?.invoiceId || downloadingInvoiceId) return;

    setDownloadingInvoiceId(invoice.invoiceId);
    try {
      const response = await axiosPrivate.get(
        `/payment/api/subscriptions/invoices/${invoice.invoiceId}/pdf`,
        { responseType: 'blob' }
      );
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `facture-${invoice.invoiceId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Erreur téléchargement facture PDF:', error);
    } finally {
      setDownloadingInvoiceId(null);
    }
  };

  const cancelSubscription = async ({ reason, comment }) => {
    await axiosPrivate.post('/payment/api/subscriptions/cancel', {
      reason,
      comment,
    });
  };

  const closeManageModal = () => {
    setIsManageModalOpen(false);
    setIsUpdatingSubscription(false);
    setUpdateSubscriptionError('');
    setSiteCount(subscribedMaxWebsites);
    setAdminCount(maxAdminCount);
    setEditorCount(maxEditorCount);
    setProjectManagerCount(maxProjectManagerCount);
  };

  const openCancelSubscriptionModal = () => {
    closeManageModal();
    setIsCancelModalOpen(true);
  };

  return (
    <div className="subscription-page">
      <h1 className="subscription-title1">
        {t('Abonnement')}
      </h1>

      <div className="subscription-content">
        <div className="subscription-content-left">
          <p className="subscription-subtitle">
            {t('Besoin d’un plan personnalisé ?')}
          </p>
          <a href="/AdminClient/Support" className="subscription-button">
            {t('Contactez-nous')}
          </a>
        </div>
        <Link to="/AdminClient/Forfait" className="subscription-all-plans-link">
          {t('Voir toutes les formules')}
        </Link>
      </div>

      <div className="subscription-pricing-wrapper">
        <div className="subscription-pricing-card">
          <div className="subscription-pricing-top">
            <div className="subscription-plan-title-row">
              <span className="subscription-plan-title">{t('Abonnement ')} {formattedPlanKey}</span>
              <span className="subscription-plan-badge">{t('Forfait actuel')}</span>
            </div>
            {hasPlanKey && normalizedPlanKey !== 'free' && normalizedPlanKey !== 'custom' && (
              <button
                type="button"
                className="subscription-manage-link"
                onClick={() => setIsManageModalOpen(true)}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.12 2.12 0 1 1 3 3L12 15l-4 1 1-4 9.5-9.5Z" />
                </svg>
                {t('Gérer mon abonnement')}
              </button>
            )}
          </div>

          <div className="subscription-plan-price-row">
            {normalizedPlanKey === 'custom' ? (
              <span className="subscription-plan-price-custom">{t('Sur mesure')}</span>
            ) : (
              <>
                <span className="subscription-plan-price-value">{formattedUnitPrice}€</span>
                <span className="subscription-plan-price-period">{t('par site/mois')}</span>
              </>
            )}
          </div>

          <div className="subscription-stats-grid">
            <div className="subscription-stat-item">
              <div className="subscription-stat-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="9" />
                  <path d="M3 12h18" />
                  <path d="M12 3a14.5 14.5 0 0 1 0 18" />
                  <path d="M12 3a14.5 14.5 0 0 0 0 18" />
                </svg>
              </div>
              <div className="subscription-stat-content">
                <span className="subscription-stat-label">{t('Sites web')}</span>
                <span className="subscription-stat-value">
                  {formatQuotaValue(currentWebsiteCount, maxWebsiteCount)}
                </span>
              </div>
            </div>

            <div className="subscription-stat-item">
              <div className="subscription-stat-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M8 3h8l-1.5 4.5h-5L8 3Z" />
                  <path d="M9.5 7.5C5 7.5 2 11 2 15.5a10 10 0 0 0 20 0c0-4.5-3-8-7.5-8" />
                  <path d="M12 11v8" />
                  <path d="M14 12.5A2 2 0 0 0 12 11h-1a2 2 0 0 0 0 4h2a2 2 0 0 1 0 4h-1a2 2 0 0 1-2-1.5" />
                </svg>
              </div>
              <div className="subscription-stat-content">
                <span className="subscription-stat-label">{t('Crédits')}</span>
                <span className="subscription-stat-value">{formatStatNumber(totalCredits)}</span>
              </div>
            </div>

            <div className="subscription-stat-item">
              <div className="subscription-stat-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M15 2H6a2 2 0 0 0-2 2v12" />
                  <path d="M18 6h-7a2 2 0 0 0-2 2v14" />
                  <path d="M12 10h4" />
                  <path d="M12 14h4" />
                  <path d="M12 18h2" />
                </svg>
              </div>
              <div className="subscription-stat-content">
                <span className="subscription-stat-label">{t('Quota contenus')}</span>
                <span className="subscription-stat-value">{formatStatNumber(totalTexts)}</span>
              </div>
            </div>
          </div>

          <div className="subscription-stats-grid subscription-stats-grid--secondary">
            <div className="subscription-stat-item">
              <div className="subscription-stat-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="8.5" cy="7" r="4" />
                  <path d="M20 8v6" />
                  <path d="M17 11h6" />
                </svg>
              </div>
              <div className="subscription-stat-content">
                <span className="subscription-stat-label">{t('Administrateurs')}</span>
                <span className="subscription-stat-value">
                  {formatQuotaValue(currentAdminCount, maxAdminCount)}
                </span>
              </div>
            </div>

            <div className={`subscription-stat-item ${maxEditorCount === 0 ? 'subscription-stat-item--disabled' : ''}`}>
              <div className="subscription-stat-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="m3 21 3-3" />
                  <path d="m6.75 17.25 10.5-10.5a2.12 2.12 0 1 1 3 3l-10.5 10.5-4.75 1.25 1.25-4.75Z" />
                  <path d="m14 6 4 4" />
                </svg>
              </div>
              <div className="subscription-stat-content">
                <span className="subscription-stat-label">{t('Rédacteurs')}</span>
                <span className="subscription-stat-value">
                  {formatQuotaValue(currentEditorCount, maxEditorCount)}
                </span>
              </div>
            </div>

            <div className={`subscription-stat-item ${maxProjectManagerCount === 0 ? 'subscription-stat-item--disabled' : ''}`}>
              <div className="subscription-stat-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="8" y="2" width="8" height="4" rx="1" />
                  <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
                  <path d="m9 14 2 2 4-4" />
                </svg>
              </div>
              <div className="subscription-stat-content">
                <span className="subscription-stat-label">{t('Chef de projet')}</span>
                <span className="subscription-stat-value">
                  {formatQuotaValue(currentProjectManagerCount, maxProjectManagerCount)}
                </span>
              </div>
            </div>
          </div>

          <div className="subscription-agents-panel">
            <div className="subscription-agents-header">
              <div className="subscription-agents-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M13 2 3 14h8l-1 8 11-12h-8l1-8Z" />
                </svg>
              </div>
              <span className="subscription-agents-title">{t('Déploiement des Agents IA')}</span>
            </div>

            <div className="subscription-agents-list">
              {websiteSummaries.length > 0 ? (
                websiteSummaries.map((website) => (
                  <div className="subscription-agent-row" key={website.websiteId}>
                    <a
                      href={buildWebsiteHref(website.websiteLabel)}
                      target="_blank"
                      rel="noreferrer"
                      className="subscription-agent-link"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                      </svg>
                      <span>{website.websiteLabel}</span>
                    </a>
                    <span className="subscription-agent-count">
                      {formatQuotaValue(website.currentAiAgents, website.maxAiAgents)}
                    </span>
                  </div>
                ))
              ) : (
                <div className="subscription-agent-row subscription-agent-row--empty">
                  <span className="subscription-agent-empty">{t('Aucun site enregistré')}</span>
                  <span className="subscription-agent-count">{formatQuotaValue(currentAiAgents, maxAiAgents)}</span>
                </div>
              )}
            </div>
          </div>

          {normalizedPlanKey !== 'custom' && (
            <>
              {normalizedPlanKey === 'pro' ? (
                <div className="subscription-upgrade-panel">
                  <div className="subscription-upgrade-text">
                    {t('Besoins spécifiques ou volumétrie importante ? Nos équipes vous accompagnent avec une offre adaptée.')}
                    <br />
                    <a href="/AdminClient/Support" className="subscription-upgrade-quote-link">
                      {t('Demander un devis')}
                    </a>
                  </div>
                </div>
              ) : currentUpgradeConfig ? (
                <div className="subscription-upgrade-panel">
                  <div className="subscription-upgrade-row">
                    <div className="subscription-upgrade-text">
                      {currentUpgradeConfig.text}
                    </div>
                    <button
                      className="subscription-upgrade-button"
                      type="button"
                      onClick={() => {
                        window.location.href = '/AdminClient/Forfait';
                      }}
                    >
                      <span>{currentUpgradeConfig.buttonLabel}</span>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <path d="M5 12h14" />
                        <path d="m12 5 7 7-7 7" />
                      </svg>
                    </button>
                  </div>

                  <div className="subscription-benefits-grid">
                    {currentUpgradeConfig.benefits.map((benefit) => (
                      <div className="subscription-benefit" key={benefit}>
                        <span className="subscription-check" aria-hidden="true">
                          ✓
                        </span>
                        {benefit}
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </>
          )}
        </div>
        <div className="subscription-credit-title">{t('Crédit AI')}</div>
        <div className="subscription-credit-card">
          <div className="subscription-credit-header">
            <div className="subscription-credit-left">
          

              <div className="subscription-credit-user">
                <div className="subscription-credit-avatar" aria-hidden="true">
                  {emailInitials}
                </div>
                <div className="subscription-credit-email">{sessionUserEmail || '-'}</div>
              </div>
            </div>

            <button
              type="button"
              className="subscription-credit-buy-button"
              onClick={() => setIsBuyCreditsModalOpen(true)}
            >
              {t('Acheter des crédits')}
            </button>
          </div>

          <div className="subscription-credit-progress-row">
            <div className="subscription-credit-progress-status">{creditStatusText}</div>

            <div
              style={{
                flex: '1 1 auto',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
              }}
            >
              <div
                className="subscription-credit-progress-track"
                aria-label="progression des crédits"
              >
                <div
                  className="subscription-credit-progress-fill"
                  style={{ width: `${creditProgressPercent}%` }}
                />
              </div>

              <div className="subscription-credit-progress-amount">{creditAmountText}</div>
             
            </div>
          </div>
        </div>
        <div className="subscription-credit-title">{t('Contenus disponibles')}</div>
        <div className="subscription-credit-card">
          <div className="subscription-credit-progress-row" style={{ marginTop: 0 }}>
            <div className="subscription-credit-progress-status">{textStatusText}</div>

            <div
              style={{
                flex: '1 1 auto',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
              }}
            >
              <div
                className="subscription-credit-progress-track"
                aria-label="progression des textes"
              >
                <div
                  className="subscription-credit-progress-fill"
                  style={{ width: `${textProgressPercent}%` }}
                />
              </div>

              <div className="subscription-credit-progress-amount">{textAmountText}</div>
            </div>
          </div>
        </div>

        <div className="subscription-invoices-card">
          <div className="subscription-invoices-title">{t('Factures')}</div>

          <div className="subscription-invoices-rows">
            {invoices.map((invoice) => (
              <div key={invoice.id} className="subscription-invoices-row">
                <div className="subscription-invoices-col subscription-invoices-col--check">
                  <input
                    type="checkbox"
                    checked={invoice.selected}
                    onChange={() => {
                      setInvoices((prev) =>
                        prev.map((i) =>
                          i.id === invoice.id
                            ? { ...i, selected: !i.selected }
                            : i
                        )
                      );
                    }}
                    aria-label={`Sélection ${invoice.month}`}
                  />
                </div>

                <div className="subscription-invoices-col subscription-invoices-col--month">
                  {invoice.month}
                </div>
                <div className="subscription-invoices-col subscription-invoices-col--amount">
                  {invoice.amount}
                </div>
                <div className="subscription-invoices-col subscription-invoices-col--formula">
                  {invoice.formula}
                </div>
                <div className="subscription-invoices-col subscription-invoices-col--sites">
                  {invoice.sites}
                </div>

                <div className="subscription-invoices-col subscription-invoices-col--actions">
                  <button
                    type="button"
                    className="subscription-invoices-action-btn"
                    onClick={() => downloadInvoicePdf(invoice)}
                    disabled={!invoice.invoiceId || downloadingInvoiceId === invoice.invoiceId}
                    aria-label={`Télécharger ${invoice.month}`}
                  >
                    <img
                      src="/Images/icon-download-simple.png"
                      alt=""
                      className="subscription-invoices-download-icon"
                    />
                  </button>
                  <button
                    type="button"
                    className="subscription-invoices-action-btn subscription-invoices-action-btn--dots"
                    aria-label={`Plus ${invoice.month}`}
                  >
                    ...
                  </button>
                </div>
              </div>
            ))}
            {invoices.length === 0 && (
              <div className="subscription-invoices-row">
                <div className="subscription-invoices-col subscription-invoices-col--check" />
                <div className="subscription-invoices-col subscription-invoices-col--month">-</div>
                <div className="subscription-invoices-col subscription-invoices-col--amount">-</div>
                <div className="subscription-invoices-col subscription-invoices-col--formula">{t('Aucune facture')}</div>
                <div className="subscription-invoices-col subscription-invoices-col--sites">-</div>
                <div className="subscription-invoices-col subscription-invoices-col--actions" />
              </div>
            )}
          </div>
        </div>
      </div>

      <SubscriptionManageModal
        open={isManageModalOpen}
        onClose={closeManageModal}
        planName={formattedPlanKey}
        canCancelSubscription={
          normalizedPlanKey !== 'free' && normalizedSubscriptionStatus !== 'CANCELED'
        }
        currentAdminCount={currentAdminCount}
        maxAdminCount={maxAdminCount}
        adminCount={adminCount}
        currentEditorCount={currentEditorCount}
        maxEditorCount={maxEditorCount}
        currentProjectManagerCount={currentProjectManagerCount}
        maxProjectManagerCount={maxProjectManagerCount}
        editorCount={editorCount}
        projectManagerCount={projectManagerCount}
        siteCount={siteCount}
        activeSiteCount={activeSiteCount}
        maxSiteCount={subscribedMaxWebsites}
        unitPrice={formattedUnitPrice}
        monthlyTotal={monthlyTotal}
        onDecrement={() => setSiteCount((v) => Math.max(activeSiteCount, v - 1))}
        onIncrement={() => setSiteCount((v) => v + 1)}
        onAdminDecrement={() => setAdminCount((v) => Math.max(currentAdminCount, v - 1))}
        onAdminIncrement={() => setAdminCount((v) => v + 1)}
        onEditorDecrement={() => setEditorCount((v) => Math.max(currentEditorCount, v - 1))}
        onEditorIncrement={() => setEditorCount((v) => v + 1)}
        onProjectManagerDecrement={() => setProjectManagerCount((v) => Math.max(currentProjectManagerCount, v - 1))}
        onProjectManagerIncrement={() => setProjectManagerCount((v) => v + 1)}
        onUpdateSubscription={updateSubscriptionQuantity}
        isUpdating={isUpdatingSubscription}
        updateError={updateSubscriptionError}
        onRequestCancelSubscription={openCancelSubscriptionModal}
      />
      <SubscriptionCancellationModal
        open={isCancelModalOpen}
        onClose={() => setIsCancelModalOpen(false)}
        onConfirm={cancelSubscription}
      />
      <BuyCreditsModal
        open={isBuyCreditsModalOpen}
        onClose={() => setIsBuyCreditsModalOpen(false)}
      />
      <PaymentStatusModal
        status={paymentStatusModal}
        onClose={closePaymentStatusModal}
        onRetry={retryPayment}
        onChangePaymentMethod={changePaymentMethod}
        onGoToDashboard={() => history.push('/AdminClient/Homepage')}
      />
    </div>
  );
};

export default Subscription;
